import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "@/lib/LangContext";
import { trackEvent } from "@/lib/usePlausible";
import { getAttribution } from "@/lib/utm";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const easeOut = [0.19, 1, 0.22, 1];

export default function Configurator() {
  const { t, lang } = useLang();
  const cfg = t.configurator;
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [form, setForm] = useState({ company: "", name: "", email: "", role: "", country: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [calendlyUrl, setCalendlyUrl] = useState("");

  React.useEffect(() => {
    axios
      .get(`${API}/config/public`)
      .then(({ data }) => setCalendlyUrl(data.calendly_url || ""))
      .catch(() => {});
  }, []);

  const totalOptionSteps = cfg.steps.length;
  const isFormStep = step === totalOptionSteps;

  const currentOptionStep = !isFormStep ? cfg.steps[step] : null;

  function pick(key, value) {
    setAnswers((a) => ({ ...a, [key]: value }));
    // auto advance
    setTimeout(() => setStep((s) => Math.min(totalOptionSteps, s + 1)), 320);
  }

  async function submit(e) {
    e.preventDefault();
    if (!form.company || !form.name || !form.email) {
      toast.error(lang === "it" ? "Compila i campi richiesti" : "Please fill the required fields");
      return;
    }
    setSubmitting(true);
    try {
      const attribution = getAttribution();
      const payload = {
        company: form.company,
        name: form.name,
        email: form.email,
        role: form.role,
        country: form.country,
        message: form.message,
        project_type: answers.project_type,
        product_category: answers.product_category,
        volume: answers.volume,
        timeline: answers.timeline,
        source: "configurator",
        ...attribution,
      };
      await axios.post(`${API}/leads`, payload);
      trackEvent("Lead Submitted", {
        project_type: answers.project_type || "",
        product_category: answers.product_category || "",
        volume: answers.volume || "",
        timeline: answers.timeline || "",
      });
      setSubmitted(true);
      toast.success(cfg.form.success);
    } catch (err) {
      console.error(err);
      toast.error(cfg.form.error);
    } finally {
      setSubmitting(false);
    }
  }

  const progress = Math.min(1, (step + (isFormStep ? 1 : 0)) / (totalOptionSteps + 1));

  return (
    <section id="configurator" className="relative py-28 lg:py-40 bg-gradient-to-b from-[#050505] via-[#08110d] to-[#050505]" data-testid="configurator-section">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14 lg:mb-16">
          <div className="lg:col-span-6">
            <div className="text-[11px] uppercase tracking-[0.28em] text-red-400 font-mono-ef">
              — {cfg.overline}
            </div>
            <h2 className="mt-6 font-display font-black text-5xl sm:text-6xl lg:text-8xl leading-[0.9] tracking-[-0.035em]">
              {cfg.title}
            </h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 lg:pt-4">
            <p className="text-lg text-white/60 leading-relaxed">{cfg.subtitle}</p>
          </div>
        </div>

        <div className="mx-auto max-w-4xl">
          {/* Progress */}
          <div className="flex items-center gap-3 mb-8" data-testid="configurator-progress">
            <div className="flex-1 h-px bg-white/10 relative overflow-hidden">
              <motion.div
                className="absolute inset-y-0 left-0 bg-red-500"
                initial={{ width: 0 }}
                animate={{ width: `${progress * 100}%` }}
                transition={{ duration: 0.6, ease: easeOut }}
              />
            </div>
            <span className="font-mono-ef text-[10px] uppercase tracking-[0.2em] text-white/50">
              {Math.min(step + 1, totalOptionSteps + 1)} / {totalOptionSteps + 1}
            </span>
          </div>

          <div className="card-elev rounded-3xl p-8 lg:p-14 min-h-[440px] relative overflow-hidden">
            <AnimatePresence mode="wait">
              {submitted ? (
                <motion.div
                  key="done"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.7, ease: easeOut }}
                  className="flex flex-col items-center justify-center text-center py-10"
                  data-testid="configurator-success"
                >
                  <div className="w-16 h-16 rounded-full bg-red-500/15 border border-red-500 flex items-center justify-center mb-6">
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" className="text-red-400">
                      <path d="M5 12l5 5 9-11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <h3 className="font-display text-3xl lg:text-4xl font-bold tracking-tight">
                    {cfg.form.success}
                  </h3>
                  <p className="mt-4 text-white/60 max-w-md">
                    {lang === "it"
                      ? "Un membro del nostro team di R&D si occuperà personalmente del tuo brief."
                      : "A member of our R&D team will personally review your brief."}
                  </p>

                  {calendlyUrl && (
                    <div className="mt-10 w-full max-w-lg p-6 lg:p-8 rounded-2xl border border-red-500/25 bg-red-500/[0.04] text-left" data-testid="calendly-block">
                      <div className="font-mono-ef text-[10px] uppercase tracking-[0.24em] text-red-400 mb-3">
                        {lang === "it" ? "Vuoi accelerare?" : "Want to move faster?"}
                      </div>
                      <h4 className="font-display text-2xl font-bold tracking-tight">
                        {lang === "it"
                          ? "Prenota una call di 30 minuti."
                          : "Book a 30-min discovery call."}
                      </h4>
                      <p className="mt-2 text-sm text-white/60 leading-relaxed">
                        {lang === "it"
                          ? "Salta la coda: parla oggi con un product manager di Emergent Food."
                          : "Skip the queue: talk today with an Emergent Food product manager."}
                      </p>
                      <a
                        href={calendlyUrl}
                        target="_blank"
                        rel="noreferrer noopener"
                        data-testid="calendly-cta"
                        className="mt-5 inline-flex btn-pill btn-primary text-sm"
                      >
                        {lang === "it" ? "Apri Calendly" : "Open Calendly"}
                        <svg className="ml-2" width="14" height="14" viewBox="0 0 24 24" fill="none">
                          <path d="M7 17L17 7M17 7H9M17 7v8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </a>
                    </div>
                  )}
                </motion.div>
              ) : isFormStep ? (
                <motion.form
                  key="form"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -30 }}
                  transition={{ duration: 0.5, ease: easeOut }}
                  onSubmit={submit}
                  className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                  data-testid="configurator-form"
                >
                  <div className="sm:col-span-2 mb-2">
                    <div className="font-mono-ef text-[10px] uppercase tracking-[0.24em] text-red-400 mb-3">
                      Step 05 · {lang === "it" ? "Contatti" : "Contact"}
                    </div>
                    <h3 className="font-display text-3xl lg:text-4xl font-bold tracking-tight">
                      {cfg.form.title}
                    </h3>
                    <p className="mt-2 text-white/50 text-sm">{cfg.form.subtitle}</p>
                  </div>
                  {[
                    { k: "company", req: true, span: "sm:col-span-2" },
                    { k: "name", req: true, span: "sm:col-span-1" },
                    { k: "email", req: true, span: "sm:col-span-1", type: "email" },
                    { k: "role", req: false, span: "sm:col-span-1" },
                    { k: "country", req: false, span: "sm:col-span-1" },
                  ].map((f) => (
                    <label key={f.k} className={`block ${f.span}`}>
                      <span className="block font-mono-ef text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">
                        {cfg.form.fields[f.k]} {f.req && <span className="text-red-400">*</span>}
                      </span>
                      <input
                        data-testid={`form-${f.k}`}
                        type={f.type || "text"}
                        required={f.req}
                        value={form[f.k]}
                        onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                        className="w-full bg-transparent border-b border-white/15 focus:border-red-400 outline-none py-3 text-white placeholder:text-white/30 font-display text-lg transition-colors"
                      />
                    </label>
                  ))}
                  <label className="block sm:col-span-2">
                    <span className="block font-mono-ef text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">
                      {cfg.form.fields.message}
                    </span>
                    <textarea
                      data-testid="form-message"
                      rows={3}
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      className="w-full bg-transparent border-b border-white/15 focus:border-red-400 outline-none py-3 text-white placeholder:text-white/30 resize-none text-base transition-colors"
                    />
                  </label>

                  <div className="sm:col-span-2 mt-4 p-4 rounded-xl bg-red-500/5 border border-red-500/25">
                    <div className="flex gap-3">
                      <span className="mt-1 w-1.5 h-1.5 rounded-full bg-red-500 shrink-0" />
                      <div>
                        <div className="font-display font-semibold text-red-300/90 text-sm">{cfg.restrictedTitle}</div>
                        <div className="text-xs text-white/55 mt-1 leading-relaxed">{cfg.restrictedText}</div>
                      </div>
                    </div>
                  </div>

                  <div className="sm:col-span-2 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 mt-6 pt-6 border-t border-white/10">
                    <button
                      type="button"
                      onClick={() => setStep((s) => Math.max(0, s - 1))}
                      className="btn-pill btn-ghost"
                      data-testid="form-back"
                    >
                      ← {cfg.back}
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="form-submit"
                      className="btn-pill btn-primary disabled:opacity-50"
                    >
                      {submitting ? cfg.form.submitting : cfg.form.submit}
                      <svg className="ml-2" width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  </div>
                </motion.form>
              ) : (
                <motion.div
                  key={`step-${step}`}
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -30 }}
                  transition={{ duration: 0.5, ease: easeOut }}
                  className="flex flex-col h-full"
                  data-testid={`configurator-step-${step}`}
                >
                  <div className="font-mono-ef text-[10px] uppercase tracking-[0.24em] text-red-400 mb-3">
                    Step {String(step + 1).padStart(2, "0")}
                  </div>
                  <h3 className="font-display text-3xl lg:text-4xl font-bold tracking-tight mb-8 max-w-2xl leading-tight">
                    {currentOptionStep.label}
                  </h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 flex-1">
                    {currentOptionStep.options.map((o) => {
                      const active = answers[currentOptionStep.key] === o.v;
                      return (
                        <button
                          key={o.v}
                          onClick={() => pick(currentOptionStep.key, o.v)}
                          data-testid={`option-${currentOptionStep.key}-${o.v}`}
                          className={`group text-left p-5 rounded-2xl border transition-all duration-300 flex items-center justify-between gap-3 ${
                            active
                              ? "border-red-400 bg-red-500/10"
                              : "border-white/10 hover:border-white/30 hover:bg-white/[0.03]"
                          }`}
                        >
                          <span className="font-display text-lg lg:text-xl font-semibold text-white">
                            {o.l}
                          </span>
                          <span
                            className={`w-6 h-6 rounded-full border flex items-center justify-center transition-colors ${
                              active ? "border-red-400 bg-red-400 text-black" : "border-white/20 text-white/40"
                            }`}
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                              <path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  <div className="flex justify-between items-center pt-8 mt-8 border-t border-white/10">
                    <button
                      onClick={() => setStep((s) => Math.max(0, s - 1))}
                      disabled={step === 0}
                      data-testid="step-back"
                      className="btn-pill btn-ghost disabled:opacity-40"
                    >
                      ← {cfg.back}
                    </button>
                    <button
                      onClick={() => setStep((s) => Math.min(totalOptionSteps, s + 1))}
                      disabled={!answers[currentOptionStep.key]}
                      data-testid="step-next"
                      className="btn-pill btn-primary disabled:opacity-40 disabled:hover:bg-[#fafafa] disabled:hover:text-black"
                    >
                      {cfg.next} →
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
